import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Plus, X, Coin, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

function fmtK(n) {
  return `K ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_STYLES = {
  draft:            "bg-[#f3f4f6] text-[#374151]",
  issued:           "bg-[#dbeafe] text-[#1e40af]",
  partially_paid:   "bg-[#fef3c7] text-[#92400e]",
  paid:             "bg-[#dcfce7] text-[#166534]",
};

const AGING_BUCKETS = [
  { k: "current",  label: "Current",     color: "#166534" },
  { k: "1_30",     label: "1-30 days",   color: "#0284c7" },
  { k: "31_60",    label: "31-60 days",  color: "#d97706" },
  { k: "61_90",    label: "61-90 days",  color: "#b45309" },
  { k: "over_90",  label: "90+ days",    color: "#991b1b" },
];

const BLANK_LINE = { description: "", quantity: 1, unit_price: "", revenue_gl_code: "4100" };

export default function AccountsReceivable() {
  const [rows, setRows] = useState([]);
  const [aging, setAging] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "new" | {kind:"detail",inv}
  const [form, setForm] = useState({
    customer_name: "", invoice_date: new Date().toISOString().slice(0,10),
    due_date: "", tax_rate_pct: 16, lines: [ { ...BLANK_LINE } ], notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [receiveAmt, setReceiveAmt] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [i, a, c] = await Promise.all([
        api.get("/ar/invoices"),
        api.get("/ar/aging"),
        api.get("/customers"),
      ]);
      setRows(i.data || []);
      setAging(a.data);
      setCustomers(c.data || []);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    let net = 0;
    for (const l of form.lines) {
      net += (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    }
    const tax = net * Number(form.tax_rate_pct || 0) / 100;
    return { net, tax, gross: net + tax };
  }, [form.lines, form.tax_rate_pct]);

  async function save() {
    if (!form.customer_name) return toast.error("Customer is required");
    const lines = form.lines
      .filter((l) => l.description && (Number(l.quantity) > 0) && (Number(l.unit_price) > 0))
      .map((l) => ({
        description: l.description,
        quantity: Number(l.quantity),
        unit_price: Number(l.unit_price),
        line_total: Number(l.quantity) * Number(l.unit_price),
        revenue_gl_code: l.revenue_gl_code || "4100",
      }));
    if (lines.length === 0) return toast.error("Add at least one line");
    setBusy(true);
    try {
      await api.post("/ar/invoices", {
        customer_name: form.customer_name,
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        tax_rate_pct: Number(form.tax_rate_pct),
        lines, notes: form.notes || null,
      });
      toast.success("Draft invoice saved");
      setModal(null);
      setForm({ ...form, customer_name: "", lines: [ { ...BLANK_LINE } ], notes: "" });
      await load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  async function issue(id) {
    try {
      await api.post(`/ar/invoices/${id}/issue`);
      toast.success("Invoice issued — journal posted");
      // Best-effort auto-email
      try {
        const { data } = await api.post(`/ar/invoices/${id}/email`);
        if (data?.ok) toast.success(`Emailed to ${data.to}`);
        else toast.info(`Email skipped — ${data?.reason || "no email on file"}`);
      } catch (e) { /* silent */ }
      await load(); setModal(null);
    } catch (e) { toast.error(formatError(e)); }
  }

  async function sendEmail(id) {
    try {
      const { data } = await api.post(`/ar/invoices/${id}/email`);
      if (data?.ok) toast.success(`Emailed to ${data.to}`);
      else toast.warning(`Not sent — ${data?.reason || "unknown"}`);
      await load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function receive(inv) {
    const amt = Number(receiveAmt || inv.amount_outstanding);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    try {
      await api.post(`/ar/invoices/${inv.id}/receive-payment`, { amount: amt });
      toast.success("Receipt posted");
      setReceiveAmt("");
      await load(); setModal(null);
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="ar-page">
      <PageHeader
        overline="Finance / AR"
        title="Customer Invoicing"
        subtitle="Issue, receive and age — every issued invoice hits the GL as revenue."
        testId="ar-header"
      />

      {aging && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          {AGING_BUCKETS.map((b) => (
            <div key={b.k} className="phizo-card p-3" data-testid={`ar-aging-${b.k}`}>
              <div className="phizo-label text-[10px]">{b.label}</div>
              <div className="mono text-lg font-black mt-1" style={{ color: b.color }}>
                {fmtK(aging.totals[b.k])}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        {aging && (
          <div className="text-xs text-[#71717a]">
            Total outstanding: <span className="mono font-bold">{fmtK(aging.grand_total)}</span>
          </div>
        )}
        <button onClick={() => setModal("new")} data-testid="ar-new-btn"
                className="ml-auto bg-black text-white px-4 py-1.5 text-xs flex items-center gap-1.5 hover:bg-[#27272a]">
          <Plus size={12} weight="bold" /> New invoice
        </button>
      </div>

      <div className="phizo-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 phizo-label text-[9px]">Number</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Customer</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Date</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Due</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Gross</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Outstanding</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-[#71717a]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#71717a]">No invoices yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} onClick={() => { setReceiveAmt(""); setModal({ kind: "detail", inv: r }); }}
                  className="border-b border-[#f4f4f5] hover:bg-[#fafafa] cursor-pointer"
                  data-testid={`ar-row-${r.number}`}>
                <td className="px-3 py-2 mono font-bold">{r.number}</td>
                <td className="px-3 py-2 font-semibold">{r.customer_name}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.invoice_date}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.due_date || "—"}</td>
                <td className="px-3 py-2 text-right mono">{fmtK(r.amount_gross)}</td>
                <td className="px-3 py-2 text-right mono font-bold">{fmtK(r.amount_outstanding)}</td>
                <td className="px-3 py-2">
                  <span className={`mono text-[10px] px-1.5 py-0.5 ${STATUS_STYLES[r.status] || ""}`}>
                    {r.status.replace("_", " ").toUpperCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New invoice modal */}
      {modal === "new" && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
               data-testid="ar-new-modal">
            <div className="sticky top-0 bg-white px-5 py-3 border-b flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">Accounts Receivable</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">New customer invoice</h3>
              </div>
              <button onClick={() => setModal(null)} className="p-1 text-[#71717a]"><X size={16} weight="bold"/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <label className="text-xs col-span-2">
                  <div className="phizo-label text-[10px] mb-1">Customer</div>
                  <input list="ar-customers" value={form.customer_name}
                         onChange={(e) => setForm({...form, customer_name: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                         data-testid="ar-form-customer"/>
                  <datalist id="ar-customers">
                    {customers.map((c) => <option key={c.id} value={c.name} />)}
                  </datalist>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">VAT %</div>
                  <input type="number" step="0.1" value={form.tax_rate_pct}
                         onChange={(e) => setForm({...form, tax_rate_pct: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Invoice date</div>
                  <input type="date" value={form.invoice_date}
                         onChange={(e) => setForm({...form, invoice_date: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Due date</div>
                  <input type="date" value={form.due_date}
                         onChange={(e) => setForm({...form, due_date: e.target.value})}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"/>
                </label>
              </div>

              <div className="border border-[#e4e4e7]">
                <table className="w-full text-xs">
                  <thead className="bg-[#fafafa]">
                    <tr className="text-left">
                      <th className="px-2 py-2 phizo-label text-[9px]">Description</th>
                      <th className="px-2 py-2 phizo-label text-[9px] text-right w-20">Qty</th>
                      <th className="px-2 py-2 phizo-label text-[9px] text-right w-28">Unit price</th>
                      <th className="px-2 py-2 phizo-label text-[9px] text-right w-32">Line total</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, idx) => (
                      <tr key={idx} className="border-t border-[#f4f4f5]">
                        <td className="px-2 py-1.5">
                          <input value={l.description}
                                 onChange={(e) => {
                                   const lines = [...form.lines];
                                   lines[idx] = { ...l, description: e.target.value };
                                   setForm({...form, lines});
                                 }}
                                 className="w-full border border-[#e4e4e7] px-2 py-1 text-xs outline-none focus:border-black"
                                 data-testid={`ar-line-desc-${idx}`}/>
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="0.01" value={l.quantity}
                                 onChange={(e) => {
                                   const lines = [...form.lines];
                                   lines[idx] = { ...l, quantity: e.target.value };
                                   setForm({...form, lines});
                                 }}
                                 className="w-full border border-[#e4e4e7] px-2 py-1 text-xs text-right mono outline-none focus:border-black"/>
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" step="0.01" value={l.unit_price}
                                 onChange={(e) => {
                                   const lines = [...form.lines];
                                   lines[idx] = { ...l, unit_price: e.target.value };
                                   setForm({...form, lines});
                                 }}
                                 className="w-full border border-[#e4e4e7] px-2 py-1 text-xs text-right mono outline-none focus:border-black"
                                 data-testid={`ar-line-price-${idx}`}/>
                        </td>
                        <td className="px-2 py-1.5 text-right mono">
                          {fmtK((Number(l.quantity)||0) * (Number(l.unit_price)||0))}
                        </td>
                        <td className="text-center">
                          <button type="button"
                                  onClick={() => setForm({...form, lines: form.lines.filter((_,i)=>i!==idx)})}
                                  disabled={form.lines.length <= 1}
                                  className="text-[#71717a] hover:text-[#dc2626] disabled:opacity-30 p-1">
                            <Trash size={12} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#fafafa] border-t-2 border-[#e4e4e7]">
                    <tr><td colSpan={3} className="px-2 py-1.5 text-right phizo-label text-[10px]">Net</td>
                      <td className="px-2 py-1.5 text-right mono font-bold">{fmtK(totals.net)}</td><td></td></tr>
                    <tr><td colSpan={3} className="px-2 py-1.5 text-right phizo-label text-[10px]">VAT ({form.tax_rate_pct}%)</td>
                      <td className="px-2 py-1.5 text-right mono">{fmtK(totals.tax)}</td><td></td></tr>
                    <tr className="bg-white"><td colSpan={3} className="px-2 py-1.5 text-right phizo-label text-[10px] uppercase font-bold">Gross</td>
                      <td className="px-2 py-1.5 text-right mono font-bold text-base">{fmtK(totals.gross)}</td><td></td></tr>
                  </tfoot>
                </table>
              </div>

              <button type="button"
                      onClick={() => setForm({...form, lines: [...form.lines, {...BLANK_LINE}]})}
                      className="border border-[#e4e4e7] text-xs px-3 py-1.5 hover:bg-[#f4f4f5]">
                <Plus size={12} weight="bold" className="inline mr-1"/> Add line
              </button>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="ar-save-btn"
                      className="bg-[#002FA7] text-white px-4 py-1.5 text-xs hover:bg-[#001f7a] disabled:opacity-60">
                {busy ? "Saving…" : "Save draft"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {modal?.kind === "detail" && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
               data-testid="ar-detail-modal">
            <div className="px-5 py-3 border-b flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">AR · {modal.inv.status.toUpperCase()}</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">
                  {modal.inv.customer_name} · <span className="mono">{modal.inv.number}</span>
                </h3>
                <div className="text-xs text-[#52525b] mt-1">
                  Issued {modal.inv.invoice_date} · due {modal.inv.due_date || "—"} ·
                  outstanding <span className="mono font-bold">{fmtK(modal.inv.amount_outstanding)}</span>
                </div>
              </div>
              <button onClick={() => setModal(null)} className="p-1 text-[#71717a]"><X size={16} weight="bold"/></button>
            </div>
            <div className="p-5">
              <table className="w-full text-xs mb-4">
                <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                  <tr className="text-left">
                    <th className="px-3 py-2 phizo-label text-[9px]">Description</th>
                    <th className="px-3 py-2 phizo-label text-[9px] text-right">Qty</th>
                    <th className="px-3 py-2 phizo-label text-[9px] text-right">Unit</th>
                    <th className="px-3 py-2 phizo-label text-[9px] text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(modal.inv.lines || []).map((l, idx) => (
                    <tr key={idx} className="border-b border-[#f4f4f5]">
                      <td className="px-3 py-2">{l.description}</td>
                      <td className="px-3 py-2 text-right mono">{l.quantity}</td>
                      <td className="px-3 py-2 text-right mono">{fmtK(l.unit_price)}</td>
                      <td className="px-3 py-2 text-right mono">{fmtK(l.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(modal.inv.status === "issued" || modal.inv.status === "partially_paid") && (
                <div className="border-t pt-3">
                  <div className="phizo-label text-[10px] mb-2">Record receipt</div>
                  <div className="flex gap-2 items-center">
                    <input type="number" step="0.01" value={receiveAmt}
                           onChange={(e) => setReceiveAmt(e.target.value)}
                           placeholder={`Full outstanding ${fmtK(modal.inv.amount_outstanding)}`}
                           className="flex-1 border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"
                           data-testid="ar-receive-amount"/>
                    <button onClick={() => receive(modal.inv)} data-testid="ar-receive-btn"
                            className="bg-[#4338ca] text-white px-4 py-1.5 text-xs hover:bg-[#3730a3] flex items-center gap-1.5">
                      <Coin size={12} weight="bold" /> Receive
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              {(modal.inv.status === "issued" || modal.inv.status === "partially_paid") && (
                <button onClick={() => sendEmail(modal.inv.id)} data-testid="ar-email-btn"
                        className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]">
                  {modal.inv.emailed_at ? "Re-email PDF" : "Email PDF"}
                </button>
              )}
              {modal.inv.status === "draft" && (
                <button onClick={() => issue(modal.inv.id)} data-testid="ar-issue-btn"
                        className="bg-[#059669] text-white px-4 py-1.5 text-xs hover:bg-[#047857] flex items-center gap-1.5">
                  <PaperPlaneTilt size={12} weight="bold" /> Issue, post & email
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
